/* eslint-disable i18next/no-literal-string */

import { keyframes } from "@emotion/react";
import {
  Alert,
  Box,
  Button,
  Icon,
  Image,
  OsStack,
  Paper,
  Theme,
  Typography,
} from "@outschool/backpack";
import { borderRadius } from "@outschool/backpack/dist-cjs/theme/borderRadius";
import { faChevronLeft, faSparkles } from "@outschool/icons";
import { captureError } from "@outschool/platform";
import { HeroLayout } from "@outschool/ui-components-classroom";
import { TrackedButton, useFeatureFlag } from "@outschool/ui-components-shared";
import { Loading, LoadingIcon } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import { navigate } from "@patched/hookrouter";
import React, { useCallback, useEffect, useRef, useState } from "react";

import Footer from "../../layout/nav/Footer";
import Header from "../../layout/nav/Header";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import {
  getCurrentLearner,
  useCurrentLearner,
} from "../../providers/CurrentLearnerProvider";
import useLoadingButtonText from "../LearnerDiscovery/useLoadingButtonText";
import AnimalSelectionGrid from "./AnimalSelectionGrid";
import goatWizardImage from "./avatar-animals/goat-wizard.webp";
import AvatarResult from "./AvatarResult";
import {
  POLLING_INTERVAL_MS,
  useCheckAvatarGenerationQuery,
  useClearAvatarsMutation,
  useStartAvatarGenerationMutation,
} from "./hooks";

const MAX_WAIT_TIME_MS = 60000; // 60 seconds

const MAX_SELECTED_ANIMALS = 3;
const MIN_SELECTED_ANIMALS = 2;

const LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

export default function AvatarBuilder() {
  // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
  useAnalyticsPage({ pageName: PageType.AvatarBuilder });
  const currentLearner = useCurrentLearner();
  const isGenerationDisabled = useFeatureFlag(
    "disable-learner-avatar-generation",
  );
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [generatedAvatar, setGeneratedAvatar] = useState<{
    name: string;
    description: string;
    imageUrl: string;
  } | null>(null);
  const isMobile = useIsMobile();

  const [clearAvatars, { loading: isClearing }] = useClearAvatarsMutation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<Error | null>(null);

  // Stores name/description from startAvatarGeneration mutation to combine with imageUrl from polling
  const avatarInfoRef = useRef<{
    name: string;
    description: string;
    animals: string[];
    generationId: string;
  } | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [startAvatarGeneration] = useStartAvatarGenerationMutation();
  const [checkAvatarGeneration] = useCheckAvatarGenerationQuery();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const pollForCompletion = useCallback(async () => {
    if (!avatarInfoRef.current) {
      return;
    }

    const { name, description, animals, generationId } = avatarInfoRef.current;
    const isTimedOut =
      pollingStartTimeRef.current &&
      Date.now() - pollingStartTimeRef.current > MAX_WAIT_TIME_MS;

    // Helper to handle terminal states (success or error)
    const finishPolling = (error?: Error, imageUrl?: string) => {
      setIsGenerating(false);
      avatarInfoRef.current = null;
      pollingStartTimeRef.current = null;

      if (error) {
        console.error("Avatar generation error:", error);
        captureError(error, { extra: { animals } });
        setGenerateError(error);
      } else if (imageUrl) {
        setGeneratedAvatar({ name, description, imageUrl });
      }
    };

    if (isTimedOut) {
      return finishPolling(new Error("Avatar generation timed out"));
    }

    try {
      const result = await checkAvatarGeneration({
        variables: { generationId, avatarName: name, animalNames: animals },
      });

      const status = result.data?.checkAvatarGeneration?.status;
      const imageUrl = result.data?.checkAvatarGeneration?.imageUrl;

      if (status === "pending") {
        pollingTimeoutRef.current = setTimeout(
          pollForCompletion,
          POLLING_INTERVAL_MS,
        );
      } else if (status === "complete" && imageUrl) {
        finishPolling(undefined, imageUrl);
      } else if (status === "failed") {
        finishPolling(new Error("Avatar generation failed"));
      } else {
        finishPolling(
          new Error("Avatar generation completed but no image URL returned"),
        );
      }
    } catch (error) {
      finishPolling(error as Error);
    }
  }, [checkAvatarGeneration]);

  const handleAnimalToggle = (animalName: string) => {
    setSelectedAnimals(prev => {
      if (prev.includes(animalName)) {
        return prev.filter(name => name !== animalName);
      }
      if (prev.length < MAX_SELECTED_ANIMALS) {
        return [...prev, animalName];
      }
      return prev;
    });
  };

  const loadingButtonText = useLoadingButtonText(isGenerating);

  const handleGenerate = async () => {
    if (selectedAnimals.length < MIN_SELECTED_ANIMALS) {
      return;
    }

    setGenerateError(null);
    setIsGenerating(true);

    try {
      // Start the generation - this returns name/description immediately
      const result = await startAvatarGeneration({
        variables: { animalNames: selectedAnimals },
      });

      const startResult = result.data?.startAvatarGeneration;
      if (!startResult?.generationId) {
        throw new Error("No generation ID returned");
      }

      // Store mutation data to combine with polling result later
      avatarInfoRef.current = {
        name: startResult.name,
        description: startResult.description,
        animals: selectedAnimals,
        generationId: startResult.generationId,
      };

      // Start polling for completion
      pollingStartTimeRef.current = Date.now();
      pollingTimeoutRef.current = setTimeout(
        pollForCompletion,
        POLLING_INTERVAL_MS,
      );
    } catch (error) {
      console.error("Failed to start avatar generation:", error);
      captureError(error as Error, {
        extra: { animals: selectedAnimals },
      });
      setGenerateError(error as Error);
      setIsGenerating(false);
    }
  };

  if (!currentLearner) {
    return <Loading />;
  }

  // Once avatar is generated, show only the result page (not the builder)
  if (generatedAvatar) {
    return (
      <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
        <AvatarResult
          avatar={generatedAvatar}
          animals={selectedAnimals}
          onBack={() => setGeneratedAvatar(null)}
        />
      </HeroLayout>
    );
  }

  const handleBack = () => {
    // Navigate back to the profile page since that's where users come from
    // when clicking the avatar edit button
    if (currentLearner) {
      navigate(`/profile/${currentLearner.uid}`);
    } else {
      // Fallback to home if no current learner
      navigate("/");
    }
  };

  // Show the builder interface for selecting animals and generating avatar
  return (
    <HeroLayout header={<Header includeBlob />} footer={<Footer />}>
      <OsStack spacing={4}>
        <Button
          startIcon={<Icon icon={faChevronLeft} />}
          size="small"
          sx={{ width: "fit-content" }}
          onClick={handleBack}
        >
          Back
        </Button>
        <Paper
          variant="elevation"
          sx={{
            borderRadius: borderRadius.medium,
            padding: { xs: "16px", md: "32px" },
          }}
        >
          <OsStack
            spacing={{ xs: 4, md: 6 }}
            direction="column"
            alignItems={isMobile ? "center" : "flex-start"}
          >
            <OsStack spacing={{ xs: 2, md: 4 }}>
              <Typography variant="h1">Create Avatar</Typography>
              <Typography variant="body1">
                Create your own awesome profile creature. Mix animals and see
                what new friend appears.
                <br />
                Pick 2 or 3 animals to mix together.
              </Typography>
            </OsStack>

            {isGenerationDisabled ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Image
                  src={goatWizardImage}
                  alt="Avatar Wizard"
                  width={isMobile ? 300 : 500}
                  height={isMobile ? 300 : 500}
                  sx={{
                    borderRadius: borderRadius.medium,
                    objectFit: "contain",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "italic",
                    textAlign: "center",
                  }}
                >
                  Looks like the Avatar Wizard needs to recharge, please come
                  back another time.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Animal Selection Grid */}
                <AnimalSelectionGrid
                  selectedAnimals={selectedAnimals}
                  onAnimalToggle={handleAnimalToggle}
                />

                {/* Mix Animals Button */}
                {generateError && (
                  <Alert
                    severity="error"
                    title="Oops, looks like the Avatar Wizard accidentally put in the wrong ingredients. Try again!"
                  />
                )}

                <OsStack direction="row" spacing={2} alignItems="center">
                  {isGenerating ? (
                    <Button
                      startIcon={<LoadingIcon />}
                      variant="contained"
                      color="primary"
                      onClick={handleGenerate}
                      disabled={true}
                      sx={(theme: Theme) => ({
                        [theme.breakpoints.up("md")]: {
                          width: "160px",
                        },
                        "&.Mui-disabled": {
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                          backgroundSize: "400% 100%",
                          animation: `${LOADING_BUTTON_GRADIENT_SHIFT_ANIMATION} 2s ease infinite`,
                          color: theme.palette.common.white,
                        },
                      })}
                    >
                      {loadingButtonText}
                    </Button>
                  ) : (
                    <TrackedButton
                      startIcon={<Icon icon={faSparkles} />}
                      variant="contained"
                      color="primary"
                      onClick={handleGenerate}
                      disabled={selectedAnimals.length < MIN_SELECTED_ANIMALS}
                      trackingName="avatar_builder.mix_creature_button_clicked"
                      trackingSharedProperties={{
                        animalCount: selectedAnimals.length,
                        animals: selectedAnimals,
                      }}
                    >
                      Mix My Creature!
                    </TrackedButton>
                  )}
                  <RemoveCreatureButton
                    isClearing={isClearing}
                    isGenerating={isGenerating}
                    onClear={async () => {
                      await clearAvatars({
                        refetchQueries: [getCurrentLearner],
                      });
                    }}
                  />
                </OsStack>
                <Typography variant="caption" color="textSecondary">
                  Removing your creature will reset your profile picture to the
                  default avatar
                </Typography>
              </>
            )}

            {/* Clear button shown even when generation is disabled */}
            {isGenerationDisabled && (
              <OsStack spacing={1} alignItems="flex-start">
                <RemoveCreatureButton
                  isClearing={isClearing}
                  onClear={async () => {
                    await clearAvatars({
                      refetchQueries: [getCurrentLearner],
                    });
                  }}
                />
                <Typography variant="caption" color="textSecondary">
                  Removing your creature will reset your profile picture to the
                  default avatar
                </Typography>
              </OsStack>
            )}
          </OsStack>
        </Paper>
      </OsStack>
    </HeroLayout>
  );
}

interface RemoveCreatureButtonProps {
  isClearing: boolean;
  isGenerating?: boolean;
  onClear: () => Promise<void>;
}

function RemoveCreatureButton({
  isClearing,
  isGenerating = false,
  onClear,
}: RemoveCreatureButtonProps) {
  return (
    <TrackedButton
      startIcon={isClearing ? <LoadingIcon /> : undefined}
      variant="outlined"
      color="secondary"
      onClick={onClear}
      disabled={isClearing || isGenerating}
      trackingName="avatar_builder.clear_avatars_button_clicked"
    >
      {isClearing ? "Removing..." : "Remove My Creature"}
    </TrackedButton>
  );
}
